import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { useTheme } from "@material-ui/core/styles";
import { useHistory, useLocation } from "react-router-dom";
import {
  makeStyles,
  Paper,
  InputBase,
  Tabs,
  Tab,
  Badge,
  IconButton,
  Typography,
  Grid,
  Tooltip,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Chip,
  Divider,
  Box,
} from "@material-ui/core";
import {
  Group,
  MoveToInbox as MoveToInboxIcon,
  CheckBox as CheckBoxIcon,
  MessageSharp as MessageSharpIcon,
  AccessTime as ClockIcon,
  Search as SearchIcon,
  Add as AddIcon,
  TextRotateUp,
  TextRotationDown,
  Android as AndroidIcon,
  Block as BlockIcon,
  RotateLeft as ResetIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import ToggleButton from "@material-ui/lab/ToggleButton";
import MarkunreadOutlinedIcon from "@material-ui/icons/MarkunreadOutlined";

import { FilterAltOff, FilterAlt, PlaylistAddCheckOutlined } from "@mui/icons-material";

import StandardTabs from "../StandardTabs";
import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsListCustom";
import TabPanel from "../TabPanel";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { TagsFilter } from "../TagsFilter";
import { UsersFilter } from "../UsersFilter";
import { StatusFilter } from "../StatusFilter";
import { WhatsappsFilter } from "../WhatsappsFilter";
import TicketsKanbanFilter from "../TicketsKanbanFilter";
import PageHelpGuide from "../PageHelpGuide";
import { TICKETS_HELP_DATA } from "../../constants/pageHelp/ticketsHelp";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { QueueSelectedContext } from "../../context/QueuesSelected/QueuesSelectedContext";
import { format, parseISO, differenceInDays } from "date-fns";
import { toast } from "react-toastify";

import api from "../../services/api";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    width: "100%",
    boxSizing: "border-box",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: "16px",
    backgroundColor: "transparent",
    border: "none",
  },

  tabsHeader: {
    minWidth: "auto",
    width: "auto",
    borderRadius: 8,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },

  settingsIcon: {
    alignSelf: "center",
    marginLeft: "auto",
    padding: theme.spacing(1),
  },

  tab: {
    minWidth: "auto",
    width: "auto",
    padding: theme.spacing(0.5, 1),
    borderRadius: 8,
    transition: "0.3s",
    border: "none",
    boxShadow: "5px 5px 10px rgba(0,0,0,0.4), -5px -5px 10px rgba(255,255,255,0.02)",
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),

    [theme.breakpoints.down("lg")]: {
      fontSize: "0.9rem",
      padding: theme.spacing(0.4, 0.8),
      marginRight: theme.spacing(0.4),
      marginLeft: theme.spacing(0.4),
    },

    [theme.breakpoints.down("md")]: {
      fontSize: "0.75rem",
      padding: theme.spacing(0.25, 0.5),
      marginRight: theme.spacing(0.25),
      marginLeft: theme.spacing(0.25),
    },

    // Específico para monitores de 11-13 polegadas
    '@media (max-width: 1366px)': {
      fontSize: "0.8rem",
      padding: theme.spacing(0.3, 0.6),
      marginRight: theme.spacing(0.3),
      marginLeft: theme.spacing(0.3),
    },

    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.1)",
    },
  },

  tabPanelItem: {
    minWidth: "33%",
    fontSize: 11,
    marginLeft: 0,
    transition: "all 0.3s ease",
    fontWeight: 500,
    borderRadius: "8px",
    margin: "0 2px",
    opacity: theme.palette.type === "light" ? 0.85 : 0.7,
    minHeight: 44,
    color: theme.palette.type === "light" ? "#475569" : "inherit",
    "&.Mui-selected": {
      opacity: 1,
      backgroundColor: theme.palette.type === "light" ? "#FFFFFF" : "transparent",
      color: theme.palette.primary.main,
      boxShadow: theme.palette.type === "light" 
        ? "0 2px 6px rgba(0,0,0,0.08)" 
        : "inset 3px 3px 6px rgba(0,0,0,0.6), inset -3px -3px 6px rgba(255,255,255,0.02)",
      textShadow: theme.palette.type === "light" ? "none" : `0 0 5px ${theme.palette.primary.main}66`,
      border: theme.palette.type === "light" ? "1px solid rgba(0, 0, 0, 0.08)" : "none",
    },
    "&:hover": {
      opacity: 1,
      backgroundColor: theme.palette.type === "light" ? "rgba(0, 0, 0, 0.04)" : `${theme.palette.primary.main}14`,
      color: theme.palette.type === "light" ? "#0F172A" : "inherit",
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: "30%",
      fontSize: 9,
      padding: theme.spacing(0.5),
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: "28%",
      fontSize: 8,
    },
  },

  tabIndicator: {
    height: 3,
    bottom: 0,
    borderRadius: "3px 3px 0 0",
    backgroundColor: theme.palette.primary.main,
    boxShadow: `0 0 8px ${theme.palette.primary.main}80`,
    transition: "all 0.3s ease",
  },
  tabsBadge: {
    top: "105%",
    right: "55%",
    transform: "translate(45%, 0)",
    whiteSpace: "nowrap",
    borderRadius: "12px",
    padding: "0 8px",
    backgroundColor: theme.palette.type === "light" ? theme.palette.primary.main : "#FFF",
    color: theme.palette.type === "light" ? "#FFF" : theme.palette.primary.main,
  },
  ticketOptionsBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: theme.palette.type === "dark" ? "#1E1E24" : "#F8FAFC",
    borderRadius: 8,
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #E2E8F0",
    boxShadow: "none",
    margin: "4px 8px 6px 8px",
    width: "calc(100% - 16px)",
    maxWidth: "calc(100% - 16px)",
    padding: "4px 8px",
    boxSizing: "border-box",
    position: "relative",
    zIndex: 10,
    overflow: "hidden",
  },

  serachInputWrapper: {
    flex: 1,
    height: 40,
    background: theme.palette.type === "dark" ? "#1E1E24" : "#F1F5F9",
    display: "flex",
    borderRadius: 40,
    padding: 4,
    border: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}40` : "1px solid #CBD5E1",
    boxShadow: "none",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    position: "relative",
    zIndex: 10,
  },

  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },

  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
    color: theme.palette.type === "light" ? "#0F172A" : "inherit",
  },

  badge: {
    // right: "-10px",
  },

  customBadge: {
    right: "-8px",
    top: "-8px",
    transform: "translate(30%, -30%)",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    textShadow: "0px 1px 2px rgba(0, 0, 0, 0.8)",
    borderRadius: "12px",
    padding: "0 6px",
    fontWeight: "bold",
    fontSize: "0.65rem",
    minWidth: "20px",
    height: "18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10,
    boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
  },

  inlineBadge: {
    backgroundColor: theme.palette.type === "dark" ? "#00E5FF" : theme.palette.primary.main,
    color: theme.palette.type === "dark" ? "#000" : "#FFF",
    borderRadius: "12px",
    padding: "2px 8px",
    fontWeight: "bold",
    fontSize: "0.7rem",
    minWidth: "24px",
    height: "22px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
    marginLeft: 6,
    marginTop: "-2px",
  },

  modernTabs: {
    minHeight: 44,
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "#1E1E24" : "#F1F5F9",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #E2E8F0",
    boxShadow: "none",
    padding: "4px 4px",
    margin: "8px",
    transition: "all 0.3s ease",
    position: "relative",
    zIndex: 10,
  },

  show: {
    display: "block",
  },

  hide: {
    display: "none !important",
  },

  closeAllFab: {
    backgroundColor: "red",
    marginBottom: "4px",
    "&:hover": {
      backgroundColor: "darkred",
    },
  },

  speedDial: {
    position: "absolute",
    bottom: theme.spacing(1),
    right: theme.spacing(1),
    "& .MuiFab-root": {
      width: "40px",
      height: "40px",
      marginTop: "4px",
    },
    "& .MuiFab-label": {
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
  },

  snackbar: {
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: theme.palette.primary.main,
    color: "white",
    borderRadius: 30,
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8em",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1em",
    },
  },

  yesButton: {
    backgroundColor: "#FFF",
    color: "rgba(0, 100, 0, 1)",
    padding: "4px 4px",
    fontSize: "1em",
    fontWeight: "bold",
    textTransform: "uppercase",
    marginRight: theme.spacing(1),
    "&:hover": {
      backgroundColor: "darkGreen",
      color: "#FFF",
    },
    borderRadius: 30,
  },
  noButton: {
    backgroundColor: "#FFF",
    color: "rgba(139, 0, 0, 1)",
    padding: "4px 4px",
    fontSize: "1em",
    fontWeight: "bold",
    textTransform: "uppercase",
    "&:hover": {
      backgroundColor: "darkRed",
      color: "#FFF",
    },
    borderRadius: 30,
  },
  filterIcon: {
    marginRight: 6,
    alignSelf: "center",
    color: theme.palette.type === "light" ? "#0872b9" : "#FFF",
    cursor: "pointer",
  },
  button: {
    height: 30,
    width: 30,
    borderRadius: 8,
    marginRight: 8,
    backgroundColor: theme.palette.type === "light" ? "#FFFFFF" : "transparent",
    border: theme.palette.type === "light" ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
    boxShadow: theme.palette.type === "light" 
      ? "0 1px 3px rgba(0,0,0,0.05)" 
      : "3px 3px 6px rgba(0,0,0,0.4), -3px -3px 6px rgba(255,255,255,0.02)",
    "&:hover": {
      boxShadow: theme.palette.type === "light" 
        ? "0 2px 5px rgba(0,0,0,0.1)" 
        : "inset 3px 3px 6px rgba(0,0,0,0.6), inset -3px -3px 6px rgba(255,255,255,0.02)",
    },
  },
  icon: {
    color: theme.palette.type === "light" ? "#64748B" : "#aaa",
    "&:hover": {
      color: theme.palette.type === "light" ? theme.palette.primary.main : "#FFF",
    },
  },
  buttonOpen: {
    "& $icon": {
      color: theme.palette.type === "light" ? theme.palette.primary.main : "#FFF",
    },
  },
  // Classe padronizada para todos os botões de ação
  standardButton: {
    height: 30,
    width: 30,
    borderRadius: 8,
    marginRight: 4,
    padding: 0,
    minWidth: 'auto',
    backgroundColor: theme.palette.type === "light" ? "#FFFFFF" : "rgba(255, 255, 255, 0.06)",
    border: theme.palette.type === "light" ? "1px solid rgba(0, 0, 0, 0.12)" : "1px solid rgba(255, 255, 255, 0.15)",
    boxShadow: theme.palette.type === "light" 
      ? "0 1px 3px rgba(0,0,0,0.05)" 
      : "0 1px 3px rgba(0,0,0,0.3)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "light" ? "#F1F5F9" : "rgba(255, 255, 255, 0.12)",
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-selected": {
      backgroundColor: `${theme.palette.primary.main}1F !important`,
      border: `1.5px solid ${theme.palette.primary.main} !important`,
      boxShadow: `0 0 8px ${theme.palette.primary.main}40 !important`,
      "&:hover": {
        backgroundColor: `${theme.palette.primary.main}2E !important`,
      },
    },
    [theme.breakpoints.down('sm')]: {
      height: 28,
      width: 28,
      marginRight: 6,
    },
    // Ajuste para monitores pequenos (11-13 polegadas)
    '@media (max-width: 1366px)': {
      height: 28,
      width: 28,
      marginRight: 6,
    },
  },
  activeButton: {
    backgroundColor: `${theme.palette.primary.main}1F !important`,
    border: `1.5px solid ${theme.palette.primary.main} !important`,
    boxShadow: `0 0 8px ${theme.palette.primary.main}40 !important`,
  },
  standardIcon: {
    color: theme.palette.type === "light" ? "#64748B" : "#CBD5E1",
    fontSize: 18,
    transition: "color 0.2s ease",
    "&:hover": {
      color: theme.palette.type === "light" ? theme.palette.primary.main : "#FFF",
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
    },
  },
  activeIcon: {
    color: `${theme.palette.primary.main} !important`,
  },
  activeFilterIcon: {
    color: theme.palette.primary.main,
    filter: `drop-shadow(0 0 6px ${theme.palette.primary.main}99)`,
  },
  filterBadge: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText || "#FFFFFF",
    fontWeight: "bold",
    fontSize: "0.68rem",
    height: 16,
    minWidth: 16,
    padding: "0 4px",
    boxShadow: `0 0 6px ${theme.palette.primary.main}cc`,
  },
  filterModalPaper: {
    maxWidth: "960px",
    width: "960px",
    borderRadius: 16,
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? `1px solid ${theme.palette.primary.main}40` : "1px solid rgba(0, 0, 0, 0.12)",
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.55)",
    overflow: "hidden",
  },
  filterModalTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px 12px 24px",
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
  },
  filterModalTitleText: {
    display: "flex",
    alignItems: "center",
    fontSize: "1.05rem",
    fontWeight: 700,
    letterSpacing: "0.3px",
    color: theme.palette.primary.main,
    "& svg": {
      fontSize: "1.3rem",
      marginRight: 8,
    },
  },
  filterModalContent: {
    padding: "12px 20px !important",
    backgroundColor: theme.palette.type === "dark" ? "#121418" : "#F8FAFC",
  },
  filterColumnBox: {
    padding: "10px 14px",
    borderRadius: 10,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.025)" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.07)" : "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: theme.palette.type === "dark" ? "none" : "0 2px 8px rgba(0,0,0,0.04)",
    height: "100%",
  },
  filterModalActions: {
    padding: "10px 20px 12px 20px",
    borderTop: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
  },
  applyButton: {
    height: 36,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: "0.82rem",
    textTransform: "none",
    padding: "6px 18px",
    backgroundColor: `${theme.palette.primary.main}1A`,
    border: `1px solid ${theme.palette.primary.main}80`,
    color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.primary.main,
    boxShadow: `0 0 8px ${theme.palette.primary.main}26`,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}2E`,
      border: `1.5px solid ${theme.palette.primary.main}`,
      boxShadow: `0 0 14px ${theme.palette.primary.main}80`,
      color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.primary.main,
    },
  },
  filterCloseButton: {
    height: 36,
    borderRadius: 8,
    fontSize: "0.82rem",
    fontWeight: 600,
    textTransform: "none",
    padding: "6px 16px",
    marginRight: 10,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid #CBD5E1",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.03)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.09)" : "#F1F5F9",
      border: theme.palette.type === "dark" ? "1.5px solid rgba(255, 255, 255, 0.45)" : "1.5px solid #94A3B8",
      boxShadow: theme.palette.type === "dark" ? "0 0 10px rgba(255, 255, 255, 0.15)" : "0 0 8px rgba(0, 0, 0, 0.1)",
      color: theme.palette.type === "dark" ? "#FFFFFF" : "#1E293B",
    },
  },
  activeChipsBarCompact: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    overflowX: "auto",
    gap: "6px",
    padding: "4px 8px",
    margin: "4px 8px 6px 8px",
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? `${theme.palette.primary.main}0D` : "rgba(0, 0, 0, 0.03)",
    border: `1px solid ${theme.palette.primary.main}33`,
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  activeChip: {
    fontSize: "0.72rem",
    height: 24,
    fontWeight: 600,
    flexShrink: 0,
    backgroundColor: `${theme.palette.primary.main}1F`,
    border: `1px solid ${theme.palette.primary.main}4D`,
    color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    "& .MuiChip-deleteIcon": {
      color: theme.palette.primary.main,
      fontSize: 16,
      "&:hover": {
        color: "#ff5252",
      },
    },
  },
  filterResetButton: {
    height: 36,
    borderRadius: 8,
    fontSize: "0.82rem",
    fontWeight: 600,
    textTransform: "none",
    padding: "6px 16px",
    color: "#ff5252",
    border: "1px solid rgba(255, 82, 82, 0.4)",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 82, 82, 0.05)" : "rgba(255, 82, 82, 0.04)",
    boxShadow: "0 0 6px rgba(255, 82, 82, 0.15)",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 82, 82, 0.15)",
      border: "1.5px solid #ff5252",
      boxShadow: "0 0 12px rgba(255, 82, 82, 0.4)",
      color: "#ff5252",
    },
    "&.Mui-disabled": {
      opacity: 0.5,
      color: theme.palette.text.disabled,
      borderColor: theme.palette.divider,
      backgroundColor: "transparent",
      boxShadow: "none",
    }
  },
  compactResetButton: {
    height: 22,
    minHeight: 22,
    maxHeight: 22,
    borderRadius: 6,
    fontSize: "0.68rem",
    fontWeight: 600,
    textTransform: "none",
    padding: "0 8px",
    color: "#ff5252",
    border: "1px solid rgba(255, 82, 82, 0.4)",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 82, 82, 0.05)" : "rgba(255, 82, 82, 0.04)",
    transition: "all 0.2s ease",
    flexShrink: 0,
    marginLeft: "auto",
    "&:hover": {
      backgroundColor: "rgba(255, 82, 82, 0.15)",
      border: "1px solid #ff5252",
      boxShadow: "0 0 8px rgba(255, 82, 82, 0.3)",
      color: "#ff5252",
    },
  },
  filterSectionSubheader: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginBottom: 10,
    display: "flex",
    alignItems: "center",
  },
  dateInput: {
    "& .MuiOutlinedInput-root": {
      minHeight: 30,
      height: 30,
      borderRadius: 8,
      fontSize: "0.80rem",
      backgroundColor: "rgba(255, 255, 255, 0.03)",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": {
        borderColor: `${theme.palette.primary.main}80`,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 8px ${theme.palette.primary.main}40`,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "4px 8px !important",
      fontSize: "0.80rem",
      height: "22px",
      boxSizing: "border-box",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.78rem",
    },
  },
}));

const TicketsManagerTabs = () => {
  const theme = useTheme();
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(true);
  const [sortTickets, setSortTickets] = useState(false);
  const [withUnreadMessages, setWithUnreadMessages] = useState(false);

  const searchInputRef = useRef();
  const [searchOnMessages, setSearchOnMessages] = useState(false);

  const { user } = useContext(AuthContext);
  const { profile } = user;
  const { setSelectedQueuesMessage } = useContext(QueueSelectedContext);
  const { tabOpen, setTabOpen } = useContext(TicketsContext);

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [groupingCount, setGroupingCount] = useState(0);
  const [ignoredCount, setIgnoredCount] = useState(0);
  const [searchCount, setSearchCount] = useState(0);

  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [forceSearch, setForceSearch] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [filter, setFilter] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedBoard, setSelectedBoard] = useState("");
  const [selectedLane, setSelectedLane] = useState("");
  const [selectedLaneTags, setSelectedLaneTags] = useState([]);
  const [kanbanLabels, setKanbanLabels] = useState({
    brandName: "",
    boardName: "",
    laneName: "",
    whatsappName: ""
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  useEffect(() => {
    setSelectedQueuesMessage(selectedQueueIds);
  }, [selectedQueueIds]);

  useEffect(() => {
    // Verificar se há parâmetro 'tab' na URL ao carregar
    const urlParams = new URLSearchParams(window.location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam && ["pending", "open", "group", "closed"].includes(tabParam)) {
      // Definir a tab principal primeiro
      if (tabParam === "pending") {
        setTab("open"); // "Aguardando" está dentro da tab "open"
      } else if (tabParam === "open") {
        setTab("open");
      } else if (tabParam === "closed") {
        setTab("closed");
      } else if (tabParam === "group") {
        setTab("open"); // "Grupos" está dentro da tab "open"
      } else if (tabParam === "ignored") {
        setTab("open"); // "Ignorados" está dentro da tab "open"
      }
      // Depois definir a sub-aba (tabOpen)
      setTimeout(() => {
        setTabOpen(tabParam);
      }, 50);
    }
  }, []);

  // Monitorar mudanças na URL para atualizar a aba quando necessário
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    if (tabParam && ["pending", "open", "group", "closed"].includes(tabParam)) {
      // Se o parâmetro tab mudou, atualizar a aba
      if (tabOpen !== tabParam) {
        // Definir a tab principal primeiro
        if (tabParam === "pending") {
          setTab("open"); // "Aguardando" está dentro da tab "open"
        } else if (tabParam === "open") {
          setTab("open");
        } else if (tabParam === "closed") {
          setTab("closed");
        } else if (tabParam === "group") {
          setTab("open"); // "Grupos" está dentro da tab "open"
        } else if (tabParam === "ignored") {
          setTab("open"); // "Ignorados" está dentro da tab "open"
        }
        // Depois definir a sub-aba (tabOpen) com um pequeno delay
        setTimeout(() => {
          setTabOpen(tabParam);
        }, 100);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
    }
    setForceSearch(!forceSearch);
  }, [tab]);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    clearTimeout(searchTimeout);

    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      setForceSearch(!forceSearch);
      if (!hasActiveFilters) {
        setTab("open");
      }
      return;
    } else if (tab !== "search") {
      setTab("search");
    }

    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
      setForceSearch(!forceSearch);
    }, 500);
  };

  const handleBack = () => {
    history.push("/tickets");
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  const handleChangeTabOpen = (e, newValue) => {
    handleBack();
    setTabOpen(newValue);
  };

  const applyPanelStyle = (status) => {
    if (tabOpen !== status) {
      return { width: 0, height: 0 };
    }
  };

  const handleSnackbarOpen = () => {
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const CloseAllTicket = async () => {
    try {
      const { data } = await api.post("/tickets/closeAll", {
        status: tabOpen,
        selectedQueueIds,
      });
      handleSnackbarClose();
    } catch (err) {
      console.log("Error: ", err);
    }
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);

    clearTimeout(searchTimeout);

    searchTimeout = setTimeout(() => {
      setSelectedTags(tags);
      setForceSearch((prev) => !prev);
    }, 500);
  };

  const handleSelectedUsers = (selecteds) => {
    const users = selecteds.map((t) => t.id);

    clearTimeout(searchTimeout);

    searchTimeout = setTimeout(() => {
      setSelectedUsers(users);
      setForceSearch((prev) => !prev);
    }, 500);
  };

  const handleSelectedWhatsapps = (selecteds) => {
    const whatsapp = selecteds.map((t) => t.id);

    clearTimeout(searchTimeout);

    searchTimeout = setTimeout(() => {
      setSelectedWhatsapp(whatsapp);
      setForceSearch((prev) => !prev);
    }, 500);
  };

  const handleSelectedStatus = (selecteds) => {
    const statusFilter = selecteds.map((t) => t.status);

    clearTimeout(searchTimeout);

    searchTimeout = setTimeout(() => {
      setSelectedStatus(statusFilter);
      setForceSearch((prev) => !prev);
    }, 500);
  };

  const hasActiveFilters = Boolean(
    selectedBrand ||
    selectedBoard ||
    selectedLane ||
    (selectedLaneTags && selectedLaneTags.length > 0) ||
    (selectedWhatsapp && selectedWhatsapp.length > 0) ||
    (selectedStatus && selectedStatus.length > 0) ||
    (selectedUsers && selectedUsers.length > 0) ||
    (selectedTags && selectedTags.length > 0) ||
    dateStart ||
    dateEnd
  );

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedBrand) count++;
    if (selectedBoard) count++;
    if (selectedLane) count++;
    if (selectedWhatsapp && selectedWhatsapp.length > 0) count++;
    if (selectedLaneTags && selectedLaneTags.length > 0) count++;
    if (selectedStatus && selectedStatus.length > 0) count++;
    if (selectedUsers && selectedUsers.length > 0) count++;
    if (selectedTags && selectedTags.length > 0) count++;
    if (dateStart || dateEnd) count++;
    return count;
  }, [
    selectedBrand,
    selectedBoard,
    selectedLane,
    selectedWhatsapp,
    selectedLaneTags,
    selectedStatus,
    selectedUsers,
    selectedTags,
    dateStart,
    dateEnd
  ]);

  const handleRemoveFilter = (filterKey) => {
    switch (filterKey) {
      case "brand":
        setSelectedBrand("");
        setSelectedBoard("");
        setSelectedLane("");
        setSelectedLaneTags([]);
        setSelectedWhatsapp([]);
        setKanbanLabels({ brandName: "", boardName: "", laneName: "", whatsappName: "" });
        break;
      case "board":
        setSelectedBoard("");
        setSelectedLane("");
        setSelectedLaneTags([]);
        setKanbanLabels((prev) => ({ ...prev, boardName: "", laneName: "" }));
        break;
      case "lane":
        setSelectedLane("");
        setSelectedLaneTags([]);
        setKanbanLabels((prev) => ({ ...prev, laneName: "" }));
        break;
      case "whatsapp":
        setSelectedWhatsapp([]);
        setKanbanLabels((prev) => ({ ...prev, whatsappName: "" }));
        break;
      case "laneTags":
        setSelectedLaneTags([]);
        break;
      case "dates":
        setDateStart("");
        setDateEnd("");
        break;
      case "status":
        setSelectedStatus([]);
        break;
      case "users":
        setSelectedUsers([]);
        break;
      case "tags":
        setSelectedTags([]);
        break;
      default:
        break;
    }
    setForceSearch((prev) => !prev);
    if (tab === "search" && activeFiltersCount <= 1 && !searchParam) {
      setTab("open");
    }
  };

  const handleKanbanFiltered = (filterData) => {
    const {
      brandId,
      brandName,
      kanbanBoardId,
      boardName,
      kanbanLaneId,
      laneName,
      whatsappId,
      whatsappName,
      laneTagIds
    } = filterData;

    setSelectedBrand(brandId || "");
    setSelectedBoard(kanbanBoardId || "");
    setSelectedLane(kanbanLaneId || "");
    setSelectedLaneTags(laneTagIds || []);
    setKanbanLabels({
      brandName: brandName || "",
      boardName: boardName || "",
      laneName: laneName || "",
      whatsappName: whatsappName || ""
    });

    if (whatsappId !== undefined) {
      if (whatsappId) {
        setSelectedWhatsapp([whatsappId]);
      } else {
        setSelectedWhatsapp([]);
      }
    }

    clearTimeout(searchTimeout);
    // Plan 1097: Preservar la pestaña de trabajo actual (open, pending, group) al filtrar comercialmente
    if (searchParam && tab !== "search") {
      setTab("search");
    }
    setForceSearch((prev) => !prev);
  };

  const handleClearAllFilters = () => {
    setSelectedBrand("");
    setSelectedBoard("");
    setSelectedLane("");
    setSelectedLaneTags([]);
    setKanbanLabels({
      brandName: "",
      boardName: "",
      laneName: "",
      whatsappName: ""
    });
    setSelectedWhatsapp([]);
    setSelectedTags([]);
    setSelectedUsers([]);
    setSelectedStatus([]);
    setDateStart("");
    setDateEnd("");
    setSearchParam("");
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    setTab("open");
    setForceSearch((prev) => !prev);
  };

  const handleFilter = () => {
    setFilterModalOpen((prev) => !prev);
  };

  const [open, setOpen] = React.useState(false);
  const [hidden, setHidden] = React.useState(false);

  const handleVisibility = () => {
    setHidden((prevHidden) => !prevHidden);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClosed = () => {
    setOpen(false);
  };

  const tooltipTitleStyle = {
    fontSize: "10px",
  };

  return (
    <Paper elevation={0} className={classes.ticketsWrapper} style={{ border: 'none', boxShadow: 'none' }}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />

      {/* Modal Flotante de Filtros Avanzados */}
      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.filterModalPaper }}
      >
        <DialogTitle disableTypography className={classes.filterModalTitle}>
          <div className={classes.filterModalTitleText}>
            <FilterListIcon />
            <span>{i18n.t("tickets.advancedFilters.title")}</span>
            {activeFiltersCount > 0 && (
              <Chip
                size="small"
                label={
                  activeFiltersCount === 1
                    ? i18n.t("tickets.advancedFilters.activeCount", { count: activeFiltersCount })
                    : i18n.t("tickets.advancedFilters.activeCountPlural", { count: activeFiltersCount })
                }
                className={classes.activeChip}
                style={{ marginLeft: 12 }}
              />
            )}
          </div>
          <IconButton
            size="small"
            onClick={() => setFilterModalOpen(false)}
            style={{ color: "rgba(255, 255, 255, 0.7)" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers className={classes.filterModalContent}>
          <Grid container spacing={2}>
            {/* Columna 1: Jerarquía Comercial (Kanban) */}
            <Grid item xs={12} md={6}>
              <div className={classes.filterColumnBox}>
                <Typography className={classes.filterSectionSubheader}>
                  {i18n.t("tickets.advancedFilters.commercialHierarchy")}
                </Typography>
                <TicketsKanbanFilter
                  brandId={selectedBrand}
                  kanbanBoardId={selectedBoard}
                  kanbanLaneId={selectedLane}
                  whatsappId={selectedWhatsapp[0] || ""}
                  laneTagIds={selectedLaneTags}
                  onFiltered={handleKanbanFiltered}
                  onReset={handleClearAllFilters}
                />
              </div>
            </Grid>

            {/* Columna 2: Criterios Generales y Fechas */}
            <Grid item xs={12} md={6}>
              <div className={classes.filterColumnBox}>
                <Typography className={classes.filterSectionSubheader}>
                  {i18n.t("tickets.advancedFilters.generalCriteriaAndDates")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`📅 ${i18n.t("tickets.dateFilter.dateStart")}`}
                      type="date"
                      value={dateStart}
                      onChange={(e) => {
                        setDateStart(e.target.value);
                        if (tab !== "search") setTab("search");
                        setForceSearch(!forceSearch);
                      }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      variant="outlined"
                      className={classes.dateInput}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={`📅 ${i18n.t("tickets.dateFilter.dateEnd")}`}
                      type="date"
                      value={dateEnd}
                      onChange={(e) => {
                        setDateEnd(e.target.value);
                        if (tab !== "search") setTab("search");
                        setForceSearch(!forceSearch);
                      }}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      size="small"
                      variant="outlined"
                      className={classes.dateInput}
                    />
                  </Grid>
                  <Grid item xs={12} sm={profile === "admin" ? 6 : 12}>
                    <StatusFilter onFiltered={handleSelectedStatus} style={{ padding: 0 }} />
                  </Grid>
                  {profile === "admin" && (
                    <Grid item xs={12} sm={6}>
                      <UsersFilter onFiltered={handleSelectedUsers} style={{ padding: 0 }} />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TagsFilter onFiltered={handleSelectedTags} tagsystem={1} />
                  </Grid>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions className={classes.filterModalActions}>
          <Button
            size="small"
            variant="outlined"
            className={classes.filterResetButton}
            onClick={handleClearAllFilters}
            startIcon={<ResetIcon style={{ fontSize: 18 }} />}
            disabled={!hasActiveFilters}
          >
            {i18n.t("tickets.advancedFilters.clearAll")}
          </Button>
          <div>
            <Button
              size="small"
              variant="outlined"
              className={classes.filterCloseButton}
              onClick={() => setFilterModalOpen(false)}
              startIcon={<CloseIcon style={{ fontSize: 18 }} />}
            >
              {i18n.t("tickets.advancedFilters.close")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              className={classes.applyButton}
              startIcon={<FilterListIcon style={{ fontSize: 18 }} />}
              onClick={() => {
                // Plan 1097: Preservar la pestaña de trabajo activa y filtrar en vivo
                if (searchParam && tab !== "search") {
                  setTab("search");
                }
                setForceSearch(prev => !prev);
                setFilterModalOpen(false);
              }}
            >
              {i18n.t("tickets.advancedFilters.applyFilters")}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
      <Box
        display="flex"
        alignItems="center"
        style={{ marginTop: 36, marginBottom: 20, paddingLeft: 28, paddingRight: 20, gap: 14 }}
      >
        <PageHelpGuide
          helpData={TICKETS_HELP_DATA}
          tooltipText="Guía y Ayuda del Inbox de Chat (Haz clic para ver)"
        />
        <Typography
          variant="h5"
          style={{
            fontWeight: 700,
            color: theme.palette.type === "dark" ? "#FFF" : theme.palette.primary.main,
            letterSpacing: "-0.5px",
          }}
        >
          {i18n.t("chat.title", "Inbox de Chat")}
        </Typography>
      </Box>
      <div className={classes.serachInputWrapper}>
        <SearchIcon className={classes.searchIcon} />
        <InputBase
          className={classes.searchInput}
          inputRef={searchInputRef}
          placeholder={i18n.t("tickets.search.placeholder")}
          type="search"
          onChange={handleSearch}
        />
        <Tooltip placement="top" title={i18n.t("tickets.search.searchOnMessages")}>
          <div>
            <Switch
              size="small"
              checked={searchOnMessages}
              onChange={(e) => { setSearchOnMessages(e.target.checked) }}
            />
          </div>
        </Tooltip>
        <Tooltip title={activeFiltersCount > 0 ? i18n.t("tickets.advancedFilters.tooltipEdit", { count: activeFiltersCount }) : i18n.t("tickets.advancedFilters.tooltipOpen")}>
          <IconButton
            style={{
              backgroundColor: "transparent",
              boxShadow: "none",
              border: "none",
              borderRadius: "50%",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
            size="small"
            variant="contained"
            aria-label="filter"
            className={classes.filterIcon}
            onClick={() => setFilterModalOpen(true)}
          >
            <Badge
              badgeContent={activeFiltersCount}
              color="secondary"
              overlap="circular"
              classes={{ badge: classes.filterBadge }}
            >
              {activeFiltersCount > 0 ? (
                <FilterAlt className={classes.activeFilterIcon} />
              ) : (
                <FilterAltOff className={classes.icon} />
              )}
            </Badge>
          </IconButton>
        </Tooltip>
      </div>

      {/* Barra compacta de chips activos en la columna lateral */}
      {hasActiveFilters && (
        <div className={classes.activeChipsBarCompact}>
          <FilterListIcon style={{ fontSize: 16, color: theme.palette.primary.main, flexShrink: 0 }} />
          <span style={{ fontSize: "0.72rem", fontWeight: 700, color: theme.palette.primary.main, marginRight: 4, flexShrink: 0 }}>
            Filtros:
          </span>
          {Boolean(selectedBrand) && (
            <Chip
              size="small"
              label={`🏷️ ${kanbanLabels.brandName || "Marca"}`}
              onDelete={() => handleRemoveFilter("brand")}
              className={classes.activeChip}
            />
          )}
          {Boolean(selectedBoard) && (
            <Chip
              size="small"
              label={`⚡ ${kanbanLabels.boardName || "Embudo"}`}
              onDelete={() => handleRemoveFilter("board")}
              className={classes.activeChip}
            />
          )}
          {Boolean(selectedLane) && (
            <Chip
              size="small"
              label={`🎯 ${kanbanLabels.laneName || "Etapa"}`}
              onDelete={() => handleRemoveFilter("lane")}
              className={classes.activeChip}
            />
          )}
          {selectedWhatsapp.length > 0 && (
            <Chip
              size="small"
              label={`📱 ${kanbanLabels.whatsappName || "Conexión"}`}
              onDelete={() => handleRemoveFilter("whatsapp")}
              className={classes.activeChip}
            />
          )}
          {selectedLaneTags.length > 0 && (
            <Chip
              size="small"
              label={`🏷️ Sub-etiquetas (${selectedLaneTags.length})`}
              onDelete={() => handleRemoveFilter("laneTags")}
              className={classes.activeChip}
            />
          )}
          {(Boolean(dateStart) || Boolean(dateEnd)) && (
            <Chip
              size="small"
              label={`📅 ${dateStart || "..."} a ${dateEnd || "..."}`}
              onDelete={() => handleRemoveFilter("dates")}
              className={classes.activeChip}
            />
          )}
          {selectedStatus.length > 0 && (
            <Chip
              size="small"
              label={`📊 Estado (${selectedStatus.length})`}
              onDelete={() => handleRemoveFilter("status")}
              className={classes.activeChip}
            />
          )}
          {selectedUsers.length > 0 && (
            <Chip
              size="small"
              label={`👤 Agentes (${selectedUsers.length})`}
              onDelete={() => handleRemoveFilter("users")}
              className={classes.activeChip}
            />
          )}
          {selectedTags.length > 0 && (
            <Chip
              size="small"
              label={`🔖 Etiquetas (${selectedTags.length})`}
              onDelete={() => handleRemoveFilter("tags")}
              className={classes.activeChip}
            />
          )}
          <Button
            size="small"
            variant="outlined"
            className={classes.compactResetButton}
            onClick={handleClearAllFilters}
          >
            Limpiar
          </Button>
        </div>
      )}

      <div className={classes.ticketOptionsBox}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', width: '100%', boxSizing: 'border-box' }}>
            <Can
              role={user.allUserChat === 'enabled' && user.profile === 'user' ? 'admin' : user.profile}
              perform="tickets-manager:showall"
              yes={() => (
                <Tooltip
                  title={showAllTickets ? i18n.t("tickets.inbox.showMyTickets") : i18n.t("tickets.inbox.showAllTickets")}
                  arrow
                  placement="bottom"
                >
                  <ToggleButton
                    className={`${classes.standardButton} ${showAllTickets ? classes.activeButton : ''}`}
                    value="uncheck"
                    selected={showAllTickets}
                    onChange={() => setShowAllTickets((prevState) => !prevState)}
                  >
                    {showAllTickets ? (
                      <VisibilityIcon className={`${classes.standardIcon} ${classes.activeIcon}`} />
                    ) : (
                      <VisibilityOffIcon className={classes.standardIcon} />
                    )}
                  </ToggleButton>
                </Tooltip>
              )}
            />
            <Snackbar
              open={snackbarOpen}
              onClose={handleSnackbarClose}
              message={i18n.t("tickets.inbox.closedAllTickets")}
              ContentProps={{
                className: classes.snackbar,
              }}
              action={
                <>
                  <Button
                    className={classes.yesButton}
                    size="small"
                    onClick={CloseAllTicket}
                  >
                    {i18n.t("tickets.inbox.yes")}
                  </Button>
                  <Button
                    className={classes.noButton}
                    size="small"
                    onClick={handleSnackbarClose}
                  >
                    {i18n.t("tickets.inbox.no")}
                  </Button>
                </>
              }
            />
            <Tooltip
              title={i18n.t("tickets.inbox.newTicketTooltip") || i18n.t("tickets.inbox.newTicket")}
              arrow
              placement="bottom"
            >
              <IconButton
                className={classes.standardButton}
                onClick={() => {
                  setNewTicketModalOpen(true);
                }}
              >
                <AddIcon className={classes.standardIcon} />
              </IconButton>
            </Tooltip>
            {user.profile === "admin" && (
              <Tooltip
                title={i18n.t("tickets.inbox.closeAllTooltip") || i18n.t("tickets.inbox.closedAll")}
                arrow
                placement="bottom"
              >
                <IconButton
                  className={classes.standardButton}
                  onClick={handleSnackbarOpen}
                >
                  <PlaylistAddCheckOutlined className={classes.standardIcon} />
                </IconButton>
              </Tooltip>
            )}
            {/* Botón "Abiertos" */}
            <Tooltip
              title={i18n.t("tickets.inbox.open")}
              arrow
              placement="bottom"
            >
              <IconButton
                className={`${classes.standardButton} ${tab === "open" ? classes.activeButton : ''}`}
                onClick={() => handleChangeTab(null, "open")}
              >
                <MoveToInboxIcon
                  className={`${classes.standardIcon} ${tab === "open" ? classes.activeIcon : ''}`}
                />
              </IconButton>
            </Tooltip>

            {/* Botón "Resueltos" */}
            <Tooltip
              title={i18n.t("tickets.inbox.resolverd")}
              arrow
              placement="bottom"
            >
              <IconButton
                className={`${classes.standardButton} ${tab === "closed" ? classes.activeButton : ''}`}
                onClick={() => handleChangeTab(null, "closed")}
              >
                <CheckBoxIcon
                  className={`${classes.standardIcon} ${tab === "closed" ? classes.activeIcon : ''}`}
                />
              </IconButton>
            </Tooltip>

            {/* Botón Solo Respondidos (No Leídos) */}
            <Tooltip
              title={withUnreadMessages ? i18n.t("tickets.inbox.allMessages") : i18n.t("tickets.inbox.onlyUnread")}
              arrow
              placement="bottom"
            >
              <IconButton
                className={`${classes.standardButton} ${withUnreadMessages ? classes.activeButton : ''}`}
                onClick={() => setWithUnreadMessages(!withUnreadMessages)}
              >
                <MarkunreadOutlinedIcon
                  className={`${classes.standardIcon} ${withUnreadMessages ? classes.activeIcon : ''}`}
                />
              </IconButton>
            </Tooltip>

            {tab !== "closed" && tab !== "search" && (
              <Tooltip
                title={!sortTickets ? i18n.t("tickets.inbox.sortAscending") : i18n.t("tickets.inbox.sortDescending")}
                arrow
                placement="bottom"
              >
                <ToggleButton
                  className={`${classes.standardButton} ${sortTickets ? classes.activeButton : ''}`}
                  value="uncheck"
                  selected={sortTickets}
                  onChange={() => setSortTickets((prevState) => !prevState)}
                >
                  {!sortTickets ? (
                    <TextRotateUp className={`${classes.standardIcon} ${sortTickets ? classes.activeIcon : ''}`} />
                  ) : (
                    <TextRotationDown className={`${classes.standardIcon} ${classes.activeIcon}`} />
                  )}
                </ToggleButton>
              </Tooltip>
            )}

            {/* Seletor de filas en la misma línea con curvatura y margen armónicos */}
            <span style={{ marginLeft: 12, flex: 1, display: "inline-flex", alignItems: "center" }}>
              <TicketsQueueSelect
                selectedQueueIds={selectedQueueIds}
                userQueues={user?.queues}
                onChange={(values) => setSelectedQueueIds(values)}
              />
            </span>
        </div>
      </div>

      <TabPanel value={tab} name="open" className={classes.ticketsWrapper}>
        <div style={{ padding: "0 8px", width: "100%", boxSizing: "border-box", marginBottom: 8 }}>
          <StandardTabs
            value={tabOpen}
            onChange={handleChangeTabOpen}
            fullWidth
            items={[
              {
                value: "open",
                label: i18n.t("ticketsList.assignedHeader"),
                badge: openCount,
                icon: <MessageSharpIcon style={{ fontSize: 17 }} />,
              },
              {
                value: "pending",
                label: i18n.t("ticketsList.pendingHeader"),
                badge: pendingCount,
                icon: <ClockIcon style={{ fontSize: 17 }} />,
              },
              ...(user?.allowGroup ? [{
                value: "group",
                label: i18n.t("ticketsList.groupingHeader"),
                badge: groupingCount,
                icon: <Group style={{ fontSize: 17 }} />,
              }] : []),
            ]}
          />
        </div>

        <Paper elevation={0} className={classes.ticketsWrapper} style={{ border: 'none', boxShadow: 'none' }}>
          <TicketsList
            status="open"
            showAll={showAllTickets}
            sortTickets={sortTickets ? "ASC" : "DESC"}
            selectedQueueIds={selectedQueueIds}
            updateCount={(val) => setOpenCount(val)}
            style={applyPanelStyle("open")}
            setTabOpen={setTabOpen}
            withUnreadMessages={withUnreadMessages}
            brandId={selectedBrand}
            kanbanBoardId={selectedBoard}
            kanbanLaneId={selectedLane}
            laneTagIds={selectedLaneTags}
            whatsappIds={selectedWhatsapp}
            users={selectedUsers}
            tags={selectedTags}
            statusFilter={selectedStatus}
            forceSearch={forceSearch}
            dateStart={dateStart}
            dateEnd={dateEnd}
          />
          <TicketsList
            status="pending"
            selectedQueueIds={selectedQueueIds}
            sortTickets={sortTickets ? "ASC" : "DESC"}
            showAll={user.profile === "admin" || user.allUserChat === 'enabled' ? showAllTickets : false}
            updateCount={(val) => setPendingCount(val)}
            style={applyPanelStyle("pending")}
            setTabOpen={setTabOpen}
            withUnreadMessages={withUnreadMessages}
            brandId={selectedBrand}
            kanbanBoardId={selectedBoard}
            kanbanLaneId={selectedLane}
            laneTagIds={selectedLaneTags}
            whatsappIds={selectedWhatsapp}
            users={selectedUsers}
            tags={selectedTags}
            statusFilter={selectedStatus}
            forceSearch={forceSearch}
            dateStart={dateStart}
            dateEnd={dateEnd}
          />
          {user.allowGroup && (
            <TicketsList
              status="group"
              showAll={showAllTickets}
              sortTickets={sortTickets ? "ASC" : "DESC"}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setGroupingCount(val)}
              style={applyPanelStyle("group")}
              setTabOpen={setTabOpen}
              withUnreadMessages={withUnreadMessages}
              brandId={selectedBrand}
              kanbanBoardId={selectedBoard}
              kanbanLaneId={selectedLane}
              laneTagIds={selectedLaneTags}
              whatsappIds={selectedWhatsapp}
              users={selectedUsers}
              tags={selectedTags}
              statusFilter={selectedStatus}
              forceSearch={forceSearch}
              dateStart={dateStart}
              dateEnd={dateEnd}
            />
          )}
        </Paper>
      </TabPanel>

      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          showAll={showAllTickets}
          selectedQueueIds={selectedQueueIds}
          setTabOpen={setTabOpen}
          brandId={selectedBrand}
          kanbanBoardId={selectedBoard}
          kanbanLaneId={selectedLane}
          laneTagIds={selectedLaneTags}
          whatsappIds={selectedWhatsapp}
          users={selectedUsers}
          tags={selectedTags}
          statusFilter={selectedStatus}
          forceSearch={forceSearch}
          dateStart={dateStart}
          dateEnd={dateEnd}
        />
      </TabPanel>

      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <div style={{ padding: '8px', textAlign: 'center', backgroundColor: 'transparent', color: 'inherit', fontSize: '12px', fontWeight: 'bold' }}>
          {i18n.t("ticketsList.called")}: {searchCount}
        </div>
        {profile === "admin" && (
          <>
            <TicketsList
              statusFilter={selectedStatus}
              searchParam={searchParam}
              showAll={showAllTickets}
              tags={selectedTags}
              users={selectedUsers}
              selectedQueueIds={selectedQueueIds}
              whatsappIds={selectedWhatsapp}
              forceSearch={forceSearch}
              searchOnMessages={searchOnMessages}
              status="search"
              dateStart={dateStart}
              dateEnd={dateEnd}
              updateCount={(val) => setSearchCount(val)}
              brandId={selectedBrand}
              kanbanBoardId={selectedBoard}
              kanbanLaneId={selectedLane}
              laneTagIds={selectedLaneTags}
            />
          </>
        )}

        {profile === "user" && (
          <TicketsList
            statusFilter={selectedStatus}
            searchParam={searchParam}
            showAll={false}
            tags={selectedTags}
            users={selectedUsers}
            selectedQueueIds={selectedQueueIds}
            whatsappIds={selectedWhatsapp}
            forceSearch={forceSearch}
            searchOnMessages={searchOnMessages}
            status="search"
            dateStart={dateStart}
            dateEnd={dateEnd}
            updateCount={(val) => setSearchCount(val)}
            brandId={selectedBrand}
            kanbanBoardId={selectedBoard}
            kanbanLaneId={selectedLane}
            laneTagIds={selectedLaneTags}
          />
        )}
      </TabPanel>
    </Paper >
  );
};

export default TicketsManagerTabs;
